import type { Geschaeftspartner, ZahlungsbedingungenStandard } from "@/lib/kontakte";
import { toast } from "@/lib/toast";

export { initialsFromName } from "@/pages/kontakte/geschaeftspartnerColumns";

export type DetailTab =
  | "uebersicht"
  | "kontakte"
  | "adressen"
  | "einkauf"
  | "bestellung"
  | "zahlung"
  | "rechnung"
  | "forderungen"
  | "projekte";

export const DETAIL_TABS: readonly DetailTab[] = [
  "uebersicht",
  "kontakte",
  "adressen",
  "einkauf",
  "bestellung",
  "zahlung",
  "rechnung",
  "forderungen",
  "projekte",
];

export function isDetailTab(value: string | null | undefined): value is DetailTab {
  return value != null && (DETAIL_TABS as readonly string[]).includes(value);
}

/** Mehrzeilige Anschrift (Zusatzzeilen, Straße, PLZ/Ort) – leere Teile werden ausgelassen. */
export function adresseLines(gp: Geschaeftspartner): string[] {
  return [
    gp.adresszeile1,
    gp.adresszeile2,
    gp.strasse,
    [gp.plz, gp.ort].filter(Boolean).join(" "),
  ]
    .map((part) => (part || "").trim())
    .filter(Boolean);
}

export function formatAdresse(gp: Geschaeftspartner): string {
  return adresseLines(gp).join(", ") || "—";
}

export function mapsUrlForAdresse(gp: Geschaeftspartner): string | null {
  const query = [gp.strasse, [gp.plz, gp.ort].filter(Boolean).join(" ")]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(", ");
  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export function formatZahlungsart(value?: string | null): string {
  if (value === "vorkasse") return "Vorkasse";
  if (value === "rechnung") return "Rechnung";
  return "—";
}

export function formatZahlungsziel(eff: ZahlungsbedingungenStandard | null | undefined): string {
  if (!eff) return "—";
  if (eff.zahlungsart === "vorkasse") return "sofort";
  return `${eff.zahlungsziel_tage} Tage`;
}

export function formatSkonto(eff: ZahlungsbedingungenStandard | null | undefined): string {
  if (!eff || !(eff.skonto_prozent > 0)) return "Kein Skonto";
  const prozent = String(eff.skonto_prozent).replace(".", ",");
  return `${prozent} % / ${eff.skontoziel_tage} Tage`;
}

/** Prüft, ob der Kunde eigene (vom Firmen-Standard abweichende) Zahlungsziele hat. */
export function hatEigeneZahlungsbedingungen(gp: Geschaeftspartner): boolean {
  const o = gp.zahlungsbedingungen;
  return Boolean(
    o &&
      (o.zahlungsart != null ||
        o.zahlungsziel_tage != null ||
        o.skonto_prozent != null ||
        o.skontoziel_tage != null),
  );
}

export async function copyToClipboard(value: string, label = "Wert"): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} kopiert.`);
  } catch {
    toast.error("Kopieren nicht möglich.");
  }
}
